/**
 * Benchmark entries API: create/list/update/delete use correct paths (POST/GET /benchmarking/:benchmarkId/entries).
 * Maps form data to backend camelCase (REQ-BM-040–042, REQ-BM-100, REQ-BM-110).
 */
import api from './api';

function toCamelCase(data: Record<string, unknown>, benchmarkId: string): Record<string, unknown> {
  return {
    benchmarkId,
    location: data.location,
    functionName: data.function_name ?? data.functionName,
    business: data.business,
    subprocess: data.subprocess,
    locationId: data.location_id ?? data.locationId,
    functionId: data.function_id ?? data.functionId,
    businessId: data.business_id ?? data.businessId,
    subprocessId: data.subprocess_id ?? data.subprocessId,
    costBucket: data.cost_bucket ?? data.costBucket ?? 'labor',
    costAmount: data.cost_amount ?? data.costAmount ?? 0,
    costDriver: data.cost_driver ?? data.costDriver,
    costDriverValue: data.cost_driver_value ?? data.costDriverValue,
    costDriverUnit: data.cost_driver_unit ?? data.costDriverUnit,
    fteCount: data.fte_count ?? data.fteCount,
    footprintSqFt: data.footprint_sq_ft ?? data.footprintSqFt,
    benchmarkProcessId: data.benchmark_process_id ?? data.benchmarkProcessId,
    benchmarkSubprocessId: data.benchmark_subprocess_id ?? data.benchmarkSubprocessId,
    laborAmount: data.labor_amount ?? data.laborAmount ?? 0,
    outsourcingAmount: data.outsourcing_amount ?? data.outsourcingAmount ?? 0,
    technologyAmount: data.technology_amount ?? data.technologyAmount ?? 0,
    materialAmount: data.material_amount ?? data.materialAmount ?? 0,
    transportationAmount: data.transportation_amount ?? data.transportationAmount ?? 0,
    occupancyAmount: data.occupancy_amount ?? data.occupancyAmount ?? 0,
    otherAmount: data.other_amount ?? data.otherAmount ?? 0,
    costNotes: data.cost_notes ?? data.costNotes,
    revenue: data.revenue,
    customerSatisfaction: data.customer_satisfaction ?? data.customerSatisfaction,
    innovationCount: data.innovation_count ?? data.innovationCount,
    qualityPct: data.quality_pct ?? data.qualityPct,
    speedValue: data.speed_value ?? data.speedValue,
    effectivenessNotes: data.effectiveness_notes ?? data.effectivenessNotes,
    complexityValue: data.complexity_value ?? data.complexityValue,
    complexityNotes: data.complexity_notes ?? data.complexityNotes,
    entryNotes: data.entry_notes ?? data.entryNotes
  };
}

export const benchmarkEntriesAPI = {
  getList(benchmarkId: string, params?: { page?: number; limit?: number; search?: string }) {
    return api
      .get(`/benchmarking/${benchmarkId}/entries`, { params })
      .then((r) => {
        const d = r.data;
        return {
          data: d?.data ?? [],
          pagination: d?.pagination ?? { page: 1, limit: 10, total: 0, totalPages: 1, hasNext: false, hasPrev: false }
        };
      });
  },

  create(benchmarkId: string, data: Record<string, unknown>) {
    const body = toCamelCase(data, benchmarkId);
    return api
      .post(`/benchmarking/${benchmarkId}/entries`, body)
      .then((r) => r.data?.data ?? r.data);
  },

  update(entryId: string, data: Record<string, unknown>) {
    const allowed = [
      'location', 'function_name', 'business', 'subprocess', 'cost_bucket', 'cost_amount',
      'cost_driver', 'cost_driver_value', 'cost_driver_unit', 'fte_count', 'footprint_sq_ft',
      'is_validated', 'validation_notes', 'benchmark_process_id', 'benchmark_subprocess_id',
      'labor_amount', 'outsourcing_amount', 'technology_amount', 'material_amount',
      'transportation_amount', 'occupancy_amount', 'other_amount', 'cost_notes',
      'revenue', 'customer_satisfaction', 'innovation_count', 'quality_pct', 'speed_value',
      'effectiveness_notes', 'complexity_value', 'complexity_notes', 'entry_notes'
    ];
    const body: Record<string, unknown> = {};
    allowed.forEach((key) => {
      if (data[key] !== undefined) body[key] = data[key];
    });
    return api.put(`/benchmarking/entries/${entryId}`, body).then((r) => r.data?.data ?? r.data);
  },

  delete(entryId: string) {
    return api.delete(`/benchmarking/entries/${entryId}`);
  }
};
